/**
 * Serializes draft CREATE across every appointment-form save path.
 *
 * The form saves the draft from three uncoordinated places — the scheduling
 * autosave, the step-save on "Next", and the legacy per-change series handlers —
 * each on its own `useAppointmentDraft` instance. Create-vs-update is decided
 * purely by the presence of a draftId, so two of these firing before the first
 * draftId exists would each POST and create a duplicate draft row for one booking.
 *
 * This coordinator holds the in-flight create in a module-level slot (shared by
 * all instances, since it lives in an imported module). While a create is in
 * flight, any other create-intent call awaits it and receives the resulting id so
 * it can UPDATE that draft instead of creating a second one.
 *
 * It is deliberately free of `fetch`, DOM, and React so it can be unit-tested as
 * pure logic: the caller passes a `performSave` that does the actual request.
 */

export type SavedDraft = { id?: string | number };

/** The in-flight create (POST), or null when none is running. Never rejects. */
let pendingCreate: Promise<SavedDraft | null> | null = null;

/**
 * @param hasDraftId  whether the caller already knows the draftId (=> update).
 * @param performSave performs the actual save. Receives the id resolved from an
 *                    in-flight create (undefined when none), and must POST when no
 *                    id is known or PUT to that id otherwise.
 * @returns the saved draft. Rejects with whatever `performSave` rejects with, so
 *          the caller keeps its existing error/abort handling.
 */
export async function coordinateDraftSave<T extends SavedDraft>(
	hasDraftId: boolean,
	performSave: (resolvedDraftId?: string) => Promise<T>,
): Promise<T> {
	// If we have no draftId but a create is already running, wait for it and reuse
	// its id so we UPDATE instead of creating a duplicate.
	let resolvedDraftId: string | undefined;
	if (!hasDraftId && pendingCreate) {
		const created = await pendingCreate;
		if (created?.id) resolvedDraftId = String(created.id);
	}

	const isCreating = !hasDraftId && !resolvedDraftId;
	const request = performSave(resolvedDraftId);

	// Publish the in-flight create synchronously — before returning/awaiting — so a
	// concurrent call observes it and waits rather than POSTing its own duplicate.
	// `.catch` keeps the shared promise non-rejecting, so waiters just fall through
	// and create when a create fails/aborts.
	if (isCreating) {
		const shared = request.catch(() => null);
		pendingCreate = shared;
		void shared.finally(() => {
			if (pendingCreate === shared) pendingCreate = null;
		});
	}

	return request;
}

/** Test-only: reset the shared in-flight slot between cases. */
export function __resetDraftCreateCoordinator(): void {
	pendingCreate = null;
}
